/**
 * O app.
 *
 * A mesma URL serve três pessoas diferentes: o visitante vê a vitrine da
 * barbearia, o cliente vê os horários dele, o barbeiro vê a agenda do dia.
 * Quem decide é a sessão — ninguém precisa escolher "sou cliente" ou "sou
 * barbeiro" numa tela de entrada.
 */

import { useQuery } from '@tanstack/react-query';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { TenantMark } from './components/TenantMark.tsx';
import { api } from './lib/api.ts';
import { useSession, useTenant } from './lib/contexts.tsx';
import { About } from './routes/About.tsx';
import { Agenda } from './routes/barber/Agenda.tsx';
import { Branding } from './routes/barber/Branding.tsx';
import { Clients } from './routes/barber/Clients.tsx';
import { Pending } from './routes/barber/Pending.tsx';
import { Services } from './routes/barber/Services.tsx';
import { WhatsappBridge } from './routes/barber/WhatsappBridge.tsx';
import { WorkingHours } from './routes/barber/WorkingHours.tsx';
import { Book } from './routes/client/Book.tsx';
import { MyAppointments } from './routes/client/MyAppointments.tsx';
import { MyProfile } from './routes/client/MyProfile.tsx';
import { NewPassword } from './routes/NewPassword.tsx';
import { Public } from './routes/Public.tsx';
import { Settings } from './routes/Settings.tsx';
import { SignIn } from './routes/SignIn.tsx';
import { SignUp } from './routes/SignUp.tsx';

export function App() {
  const session = useSession();
  const isStaff = session?.membership != null;
  const signedIn = session != null;

  return (
    <div className="flex h-full flex-col bg-surface">
      <main className="flex-1 overflow-y-auto scroll-touch">
        <Routes>
          <Route path="/entrar" element={<SignIn />} />
          <Route path="/cadastro" element={<SignUp />} />
          <Route path="/nova-senha" element={<NewPassword />} />
          <Route path="/ajustes" element={<Settings />} />
          {/* Ao lado de Ajustes e fora dos ramos: a versão que está no aparelho
              é a mesma pergunta para a equipe, para o cliente e para quem ainda
              nem entrou — e é justamente quando o app não funciona que ela é
              feita. */}
          <Route path="/sobre" element={<About />} />
          {/* Fora do ramo do cliente: o barbeiro também corrige o próprio
              nome e telefone, e a conta é a mesma dos dois lados. */}
          {signedIn && <Route path="/meus-dados" element={<MyProfile />} />}

          {isStaff ? (
            <>
              <Route path="/" element={<Agenda />} />
              <Route path="/pedidos" element={<Pending />} />
              <Route path="/clientes" element={<Clients />} />
              <Route path="/servicos" element={<Services />} />
              <Route path="/identidade" element={<Branding />} />
              <Route path="/expediente" element={<WorkingHours />} />
              <Route path="/whatsapp" element={<WhatsappBridge />} />
            </>
          ) : session ? (
            <>
              <Route path="/" element={<MyAppointments />} />
              <Route path="/agendar" element={<Book />} />
            </>
          ) : (
            <Route path="/" element={<Public />} />
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {session && <BottomNav isStaff={isStaff} />}
    </div>
  );
}

/**
 * Navegação embaixo, no alcance do polegar.
 *
 * O crachá de pendentes fica aqui porque é a informação que faz o barbeiro
 * abrir o app: alguém pediu horário e está esperando resposta.
 */
function BottomNav({ isStaff }: { isStaff: boolean }) {
  const location = useLocation();

  const { data } = useQuery({
    queryKey: ['pending-count'],
    queryFn: () => api.get<{ appointments: unknown[] }>('/appointments/pending'),
    enabled: isStaff,
    refetchInterval: 60_000,
  });

  const pending = data?.appointments.length ?? 0;

  const items = isStaff
    ? [
        { to: '/', label: 'Agenda', icon: '▤' },
        { to: '/pedidos', label: 'Pedidos', icon: '◷', badge: pending },
        { to: '/clientes', label: 'Clientes', icon: '☰' },
        { to: '/ajustes', label: 'Ajustes', icon: '⚙' },
      ]
    : [
        { to: '/', label: 'Meus horários', icon: '▤' },
        { to: '/agendar', label: 'Agendar', icon: '＋' },
        { to: '/ajustes', label: 'Ajustes', icon: '⚙' },
      ];

  // Nas telas de entrada a barra só atrapalha.
  if (['/entrar', '/cadastro', '/nova-senha'].includes(location.pathname)) return null;

  return (
    <nav className="pb-safe shrink-0 border-t border-line bg-surface">
      <ul className="flex">
        {items.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex touch-target flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium ${
                  isActive ? 'text-primary' : 'text-ink-faint'
                }`
              }
            >
              <span className="relative text-[19px] leading-none">
                {item.icon}
                {item.badge ? (
                  <span className="absolute -top-1 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-ink">
                    {item.badge}
                  </span>
                ) : null}
              </span>
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Cabeçalho comum das telas internas. */
export function ScreenHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  const { tenant } = useTenant();

  return (
    <header className="pt-safe sticky top-0 z-30 border-b border-line bg-surface/95 px-4 pb-3 backdrop-blur">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate text-[12px] font-medium text-ink-faint">
            <TenantMark size={16} framed={false} />
            {tenant.name}
          </p>
          <h1 className="truncate text-[22px] leading-tight font-bold text-ink">{title}</h1>
          {subtitle && <p className="truncate text-[13px] text-ink-soft">{subtitle}</p>}
        </div>
        {action}
      </div>
    </header>
  );
}
